// src/components/UserContext.jsx
import React, { createContext, useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  }, []);

  const refreshUser = useCallback(
    async (authToken) => {
      const tokenToUse = authToken || token;
      if (!tokenToUse) return null;

      try {
        const res = await fetch(`${API_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${tokenToUse}`,
          },
        });

        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            logout();
          }
          return null;
        }

        const data = await res.json();
        setUser(data);
        localStorage.setItem("user", JSON.stringify(data));
        return data;
      } catch (error) {
        console.error("Failed to refresh user profile", error);
        return null;
      }
    },
    [logout, token]
  );

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.warn("Invalid stored user data", error);
        localStorage.removeItem("user");
      }
    }

    if (storedToken) {
      setToken(storedToken);
      refreshUser(storedToken).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [refreshUser]);

  const login = useCallback(
    (userData, authToken) => {
      setUser(userData);
      setToken(authToken);
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("token", authToken);
      refreshUser(authToken);
    },
    [refreshUser]
  );

  const updateUser = useCallback((userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  }, []);

  return (
    <UserContext.Provider
      value={{ user, token, login, logout, updateUser, refreshUser, loading }}
    >
      {children}
    </UserContext.Provider>
  );
};